
"use client";

import { createContext, useContext, useEffect, useState, type ReactNode, useCallback } from 'react';
import { db } from '@/lib/firebase';
import { collection, getDocs, addDoc, doc, deleteDoc, query, orderBy } from 'firebase/firestore';
import { useToast } from '@/hooks/use-toast';

export type OfferFilter = {
  id: string;
  phrase: string;
};

interface OfferFilterContextType {
  offerFilters: OfferFilter[];
  addOfferFilter: (filter: Omit<OfferFilter, 'id'>) => Promise<void>;
  deleteOfferFilter: (id: string) => Promise<void>;
  loading: boolean;
  refreshOfferFilters: () => void;
}

const OfferFilterContext = createContext<OfferFilterContextType | undefined>(undefined);

export const OfferFilterProvider = ({ children }: { children: ReactNode }) => {
  const [offerFilters, setOfferFilters] = useState<OfferFilter[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchOfferFilters = useCallback(async () => {
    setLoading(true);
    try {
      const filtersCollection = collection(db, 'offerFilters');
      const q = query(filtersCollection, orderBy('phrase', 'asc'));
      const snapshot = await getDocs(q);

      const filterList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
      } as OfferFilter));
      setOfferFilters(filterList);

    } catch (error) {
      console.error("Failed to fetch offer filters from Firestore", error);
      toast({ variant: 'destructive', title: "Error", description: "Could not load offer filters from the database." });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchOfferFilters();
  }, [fetchOfferFilters]);

  const addOfferFilter = async (filterData: Omit<OfferFilter, 'id'>) => {
    try {
      const filtersCollection = collection(db, 'offerFilters');
      const docRef = await addDoc(filtersCollection, filterData);
      setOfferFilters(prev => [...prev, {...filterData, id: docRef.id}].sort((a,b) => a.phrase.localeCompare(b.phrase)));
    } catch (error) {
      console.error("Error adding offer filter:", error);
      toast({ variant: 'destructive', title: "Error", description: "Could not add the filter phrase." });
      throw error;
    }
  };

  const deleteOfferFilter = async (id: string) => {
    try {
      const filterDocRef = doc(db, 'offerFilters', id);
      await deleteDoc(filterDocRef);
      setOfferFilters(prev => prev.filter(f => f.id !== id));
    } catch (error) {
       console.error("Error deleting offer filter:", error);
       toast({ variant: 'destructive', title: "Error", description: "Could not delete the filter phrase." });
       throw error;
    }
  };

  return (
    <OfferFilterContext.Provider value={{ offerFilters, addOfferFilter, deleteOfferFilter, loading, refreshOfferFilters: fetchOfferFilters }}>
      {children}
    </OfferFilterContext.Provider>
  );
};

export const useOfferFilters = () => {
  const context = useContext(OfferFilterContext);
  if (context === undefined) {
    throw new Error('useOfferFilters must be used within an OfferFilterProvider');
  }
  return context;
};
